import { ENDED_BY, PROJECT_STATUS_WORDS, statusRefusal, type EndedBy, type ProjectDto } from '@nsb/core';
import { useState } from 'react';
import { useNavigate } from 'react-router';
import { api, errorText, queryClient } from '../../api.ts';
import { useFeedbackRecord } from '../../components/feedback-context.ts';
import { PlaceInput } from '../../components/PlaceInput.tsx';
import { Button, Confirm, ErrorLine, Field, LoadError, Loading, NumberInput, Page, Section, TextInput, Title, Toggle } from '../../components/ui.tsx';
import { homeSection } from '../../components/homeWords.ts';
import { useUnsaved } from '../../components/useUnsaved.ts';
import { isAdmin, useMe } from '../../session.tsx';
import { settingsChanges } from '../formChanges.ts';
import { ProjectCrumb } from './common.tsx';
import { endedWords } from './ended.ts';
import { useProject, useProjectId } from './hooks.ts';
import { easternToday } from '../../days.ts';

export function ProjectSettings() {
  const projectId = useProjectId();
  const { data: project, error } = useProject(projectId);
  useFeedbackRecord(projectId, project ? { type: 'project', id: projectId, label: `${project.name} — settings` } : null);
  if (error) return <LoadError error={error} />;
  if (!project) {
    return (
      <Page>
        <Loading />
      </Page>
    );
  }
  return <SettingsForm key={editedFields(project)} project={project} />;
}

/** What the form edits: it starts fresh when these change (a save, another tab), never for anything else about the project. */
const editedFields = (p: ProjectDto) =>
  JSON.stringify([p.id, p.name, p.code, p.status, p.siteAddress, p.sitePlace, p.jurisdiction, p.sourceNote, p.summary, p.feePctOverride, p.contingencyPctOverride, p.clientShows]);

function SettingsForm({ project }: { project: ProjectDto }) {
  const navigate = useNavigate();
  const [f, setF] = useState(project);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const save = async () => {
    setBusy(true);
    setErr(null);
    try {
      await api(`/projects/${project.id}`, {
        method: 'PATCH',
        body: {
          name: f.name,
          code: f.code || null,
          siteAddress: f.siteAddress ?? '',
          sitePlace: f.sitePlace,
          jurisdiction: f.jurisdiction ?? '',
          sourceNote: f.sourceNote ?? '',
          summary: f.summary ?? '',
          feePctOverride: f.feePctOverride,
          contingencyPctOverride: f.contingencyPctOverride,
          ...(f.clientShows ? { clientShows: f.clientShows } : {}),
        },
      });
      await queryClient.invalidateQueries({ queryKey: ['project', project.id] });
      await queryClient.invalidateQueries({ queryKey: ['pipeline'] });
    } catch (e) {
      setErr(errorText(e));
    } finally {
      setBusy(false);
    }
  };

  const text = (k: 'name' | 'code' | 'siteAddress' | 'jurisdiction' | 'sourceNote') => (e: React.ChangeEvent<HTMLInputElement>) => setF({ ...f, [k]: e.target.value });
  // Changes not saved, on the bar at the window's foot, which saves as Save below does (P-29).
  useUnsaved({ names: settingsChanges(project, f), busy, error: err, onSave: save, onDiscard: () => (setF(project), setErr(null)) });

  return (
    <Page>
      <ProjectCrumb project={project} />
      <Title>Settings</Title>
      <Field label="Name">{(id) => <TextInput id={id} value={f.name} onChange={text('name')} />}</Field>
      <Field label="Project code" hint="Editable. Files are keyed by the internal id, so a rename never orphans anything.">
        {(id) => <TextInput id={id} value={f.code ?? ''} onChange={text('code')} />}
      </Field>
      {/* Where it is and its status are read, never chosen (Jason, 2026-10-06; Still to decide, A). */}
      <div className="mb-5 grid gap-x-6 gap-y-4 sm:grid-cols-2">
        <div>
          <span className="label block">Where it is</span>
          <p className="mt-2">{project.stage ? project.stage.name : project.convertedAt ? 'Nothing on its process yet' : 'A lead'}</p>
          <p className="meta mt-1">{project.convertedAt ? 'From its process: the earliest phase with a step not done.' : 'It is a project once it is started.'}</p>
        </div>
        <div>
          <span className="label block">Status</span>
          <p className="mt-2">{project.ended ? `Cancelled ${endedWords(project.ended)}` : PROJECT_STATUS_WORDS[project.status]}</p>
          {project.ended?.reason ? <p className="meta mt-1">{project.ended.reason}</p> : null}
        </div>
      </div>
      <div className="grid gap-x-6 sm:grid-cols-2">
        <Field label="Site address">
          {(id) => <PlaceInput id={id} value={f.siteAddress ?? ''} place={f.sitePlace} onChange={(t, place) => setF({ ...f, siteAddress: t, sitePlace: place })} />}
        </Field>
        <Field label="Jurisdiction">{(id) => <TextInput id={id} value={f.jurisdiction ?? ''} onChange={text('jurisdiction')} />}</Field>
      </div>
      <Field label="Source">{(id) => <TextInput id={id} value={f.sourceNote ?? ''} onChange={text('sourceNote')} />}</Field>
      <Field label="Goal" hint="What it is for, in a sentence or two. Its picture on the Projects page is painted from this and the name, again whenever either changes.">
        {(id) => <textarea id={id} rows={3} className="field resize-y" value={f.summary ?? ''} onChange={(e) => setF({ ...f, summary: e.target.value })} />}
      </Field>
      <Section title="Fee and contingency">
        <p className="mb-4 text-sm text-ink-3">
          Inherited from the organization ({project.feePct}% / {project.contingencyPct}% today). Set a number here only to override it for this project. A new contingency is proposed to the client when they have a budget, at the same amount with the contingency where it was, split anew, for them to confirm.
        </p>
        <div className="grid grid-cols-2 gap-6">
          <Field label="Fee override %">{(id) => <NumberInput id={id} value={f.feePctOverride} onChange={(v) => setF({ ...f, feePctOverride: v })} placeholder="inherit" />}</Field>
          <Field label="Contingency override %">
            {(id) => <NumberInput id={id} value={f.contingencyPctOverride} onChange={(v) => setF({ ...f, contingencyPctOverride: v })} placeholder="inherit" />}
          </Field>
        </div>
      </Section>
      {project.processKind && !project.ended ? <WhoseProject project={project} /> : null}
      {f.clientShows && project.processKind !== 'internal' ? (
        <Section title="What the client sees">
          <p className="mb-4 max-w-measure text-sm text-ink-3">
            The files and design reviews you share with them and what is waiting on them are always theirs. The rest is shown part by part: the process stays NSB’s draft until you have proofread it.
          </p>
          <div className="space-y-1">
            {(
              [
                ['price', 'The price', 'The sentence and the cost bar.'],
                ['funnel', 'The funnel', 'How the price firms up, with its dates and seasons.'],
                ['process', 'The process', `${homeSection('working')} and the ${homeSection('tree')}: every step of the job, done, under way or not started yet.`],
              ] as const
            ).map(([k, label, what]) => (
              <label key={k} className="flex min-h-11 items-start gap-3 py-1.5">
                <input
                  type="checkbox"
                  className="mt-1.5"
                  checked={f.clientShows![k]}
                  onChange={(e) => setF({ ...f, clientShows: { ...f.clientShows!, [k]: e.target.checked } })}
                />
                <span>
                  <span className="block">{label}</span>
                  <span className="block text-sm text-ink-3">{what}</span>
                </span>
              </label>
            ))}
          </div>
        </Section>
      ) : null}
      <ErrorLine error={err} />
      <div className="mt-6">
        <Button onClick={() => void save()} busy={busy}>
          Save
        </Button>
      </div>
      <ProjectStatus project={project} />
      {statusRefusal({ status: project.status, started: project.convertedAt !== null }, 'cancelled') === null ? <CancelProject project={project} /> : null}
      <DeleteProject project={project} onDeleted={() => navigate('/')} />
    </Page>
  );
}

/**
 * A client project, or an internal one, NSB's own (Jason, 2026-10-03:
 * "shouldn't there be an internal flag on a project in settings?"), switched
 * here. A job's sign-offs are steps added by hand, so the switch changes its
 * money alone: its process stays as it is.
 */
function WhoseProject({ project }: { project: ProjectDto }) {
  const internal = project.processKind === 'internal';
  const [asking, setAsking] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const flip = async () => {
    setBusy(true);
    setErr(null);
    try {
      await api(`/projects/${project.id}/process/kind`, { body: { kind: internal ? 'client' : 'internal' } });
      await queryClient.invalidateQueries({ queryKey: ['project', project.id] });
      setAsking(false);
    } catch (e) {
      setErr(errorText(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Section title="Whose project">
      <Toggle
        checked={internal}
        onChange={() => setAsking(true)}
        label="An internal project, NSB’s own"
        hint={
          internal
            ? 'No client: its price is its cost alone, with no fee, contingency, agreed price or budget.'
            : 'A client project: its price carries the fee and contingency, an agreed price and the client’s budget.'
        }
      />
      {asking ? (
        <Confirm
          prompt={
            internal
              ? 'Make it a client project? Its price carries the fee and contingency again. Its process stays as it is.'
              : 'Make it internal? Its price becomes its cost alone. Its process stays as it is.'
          }
          busy={busy}
          onYes={() => void flip()}
          onNo={() => setAsking(false)}
        />
      ) : null}
      <ErrorLine error={err} />
    </Section>
  );
}

/**
 * Its status, by what happens (Jason, 2026-10-06: "status should not be selectable, it has to be triggered by a
 * project level event ... like a pause or cancellation require certain things, active is default, archived has to
 * happen after the project is completed, or canceled"): Pause with why, Resume, and Archive once it is completed or
 * cancelled (a lead whenever NSB is done with it). Completed comes with Final payment and sign-off; Cancel is below.
 */
function ProjectStatus({ project }: { project: ProjectDto }) {
  const [asking, setAsking] = useState<'pause' | 'resume' | 'archive' | null>(null);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  // The buttons the status door takes (core `statusRefusal`), never one it would refuse.
  const facts = { status: project.status, started: project.convertedAt !== null };
  const can = { pause: !statusRefusal(facts, 'paused'), resume: !statusRefusal(facts, 'resumed'), archive: !statusRefusal(facts, 'archived') };
  if (project.status === 'archived') return null;
  const run = async (path: 'pause' | 'resume' | 'archive') => {
    setBusy(true);
    setErr(null);
    try {
      await api(`/projects/${project.id}/${path}`, { body: path === 'archive' ? {} : { reason: reason.trim() || undefined } });
      await queryClient.invalidateQueries({ queryKey: ['project', project.id] });
      await queryClient.invalidateQueries({ queryKey: ['pipeline'] });
      setAsking(null);
      setReason('');
    } catch (e) {
      setErr(errorText(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Section title="Status">
      <p className="mb-4 max-w-measure text-sm text-ink-3">
        It changes by what happens, never by choosing: paused and resumed with why, completed when Final payment and sign-off is done, cancelled below. Archive it once it is completed or cancelled to take it off the Projects page.
      </p>
      {asking === 'pause' || asking === 'resume' ? (
        <div className="max-w-measure">
          <Field label={asking === 'pause' ? 'Why it is paused' : 'Why it is picked up again'} hint={asking === 'pause' ? 'Kept on its timeline, for NSB.' : 'If you like; kept on its timeline, for NSB.'}>
            {(id) => <TextInput id={id} value={reason} onChange={(e) => setReason(e.target.value)} />}
          </Field>
          <div className="flex flex-wrap gap-4">
            <Button busy={busy} disabled={asking === 'pause' && !reason.trim()} onClick={() => void run(asking)}>
              {asking === 'pause' ? 'Pause the project' : 'Resume the project'}
            </Button>
            <Button tone="quiet" onClick={() => setAsking(null)}>
              Not now
            </Button>
          </div>
        </div>
      ) : asking === 'archive' ? (
        <Confirm prompt={`Archive ${project.name}? It leaves the Projects page and is kept, as everything is.`} busy={busy} onYes={() => void run('archive')} onNo={() => setAsking(null)} />
      ) : (
        <div className="flex flex-wrap gap-4">
          {can.pause ? (
            <Button tone="secondary" onClick={() => setAsking('pause')}>
              Pause…
            </Button>
          ) : null}
          {can.resume ? (
            <Button tone="secondary" onClick={() => setAsking('resume')}>
              Resume…
            </Button>
          ) : null}
          {can.archive ? (
            <Button tone="secondary" onClick={() => setAsking('archive')}>
              Archive
            </Button>
          ) : null}
        </div>
      )}
      <ErrorLine error={err} />
    </Section>
  );
}

/**
 * Delete project (Jason, 2026-10-07: "a special link in a project settings in admin ... it asks for typing delete and
 * say that it cant be restored, unarchived, or reversed"): an admin's, at the foot of the page, a link that opens the
 * one question. Typing delete is the confirmation; the project and everything on it go from the database, for good.
 */
function DeleteProject({ project, onDeleted }: { project: ProjectDto; onDeleted: () => void }) {
  const { data: me } = useMe();
  const [open, setOpen] = useState(false);
  const [word, setWord] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  if (!isAdmin(me)) return null;
  const ready = word.trim().toLowerCase() === 'delete';
  const del = async () => {
    setBusy(true);
    setErr(null);
    try {
      await api(`/projects/${project.id}`, { method: 'DELETE', body: { confirm: word.trim() } });
      queryClient.removeQueries({ queryKey: ['project', project.id] });
      await queryClient.invalidateQueries({ queryKey: ['pipeline'] });
      onDeleted();
    } catch (e) {
      setErr(errorText(e));
      setBusy(false);
    }
  };
  if (!open) {
    return (
      <p className="mt-14 border-t border-rule pt-4">
        <button type="button" className="hit link text-sm text-alert" onClick={() => setOpen(true)}>
          Delete project
        </button>
      </p>
    );
  }
  return (
    <Section title="Delete project">
      <p className="mb-4 max-w-measure text-sm">
        This deletes {project.name} and everything on it: its process, files, timeline, money, people and email kept with it, and its clients' access to it.{' '}
        <strong>It can't be restored, unarchived or reversed.</strong>
      </p>
      <Field label="Type delete to confirm">
        {(id) => <TextInput id={id} value={word} autoComplete="off" onChange={(e) => setWord(e.target.value)} />}
      </Field>
      <ErrorLine error={err} />
      <div className="mt-4 flex flex-wrap items-center gap-4">
        <Button tone="danger" disabled={!ready} busy={busy} onClick={() => void del()}>
          Delete {project.name} for good
        </Button>
        <button type="button" className="hit link text-sm text-ink-2" onClick={() => { setOpen(false); setWord(''); setErr(null); }}>
          Keep it
        </button>
      </div>
    </Section>
  );
}

/** Ending a project before it is finished: who ended it, when and why. Final; admins only. */
function CancelProject({ project }: { project: ProjectDto }) {
  const { data: me } = useMe();
  const [by, setBy] = useState<EndedBy>('client');
  const [reason, setReason] = useState('');
  const [on, setOn] = useState(easternToday);
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  if (!isAdmin(me)) return null;
  const cancel = async () => {
    setBusy(true);
    setErr(null);
    try {
      await api(`/projects/${project.id}/cancel`, { body: { by, reason: reason.trim(), on } });
      await queryClient.invalidateQueries({ queryKey: ['project', project.id] });
      await queryClient.invalidateQueries({ queryKey: ['pipeline'] });
    } catch (e) {
      setErr(errorText(e));
      setConfirming(false);
    } finally {
      setBusy(false);
    }
  };
  return (
    <Section title="Cancellation">
      <p className="mb-4 text-sm text-ink-3">
        For a project that ends before it is finished. Nothing waits on the client after that. It cannot be undone; archive it later to take it off the Projects page.
      </p>
      <fieldset className="mb-5">
        <legend className="label">Who ended it</legend>
        <div className="mt-2 flex gap-6">
          {ENDED_BY.map((b) => (
            <label key={b} className="flex min-h-11 items-center gap-2">
              <input type="radio" name="ended-by" checked={by === b} onChange={() => setBy(b)} />
              {b === 'client' ? 'The client' : 'NSB'}
            </label>
          ))}
        </div>
      </fieldset>
      <Field label="Why" hint="Kept for NSB; the client sees only the date.">
        {(id) => <TextInput id={id} value={reason} onChange={(e) => setReason(e.target.value)} />}
      </Field>
      <Field label="Ended on">{(id) => <TextInput id={id} type="date" value={on} onChange={(e) => setOn(e.target.value)} />}</Field>
      <ErrorLine error={err} />
      {confirming ? (
        <Confirm prompt={`Cancel ${project.name} for good?`} busy={busy} onYes={() => void cancel()} onNo={() => setConfirming(false)} />
      ) : (
        <Button tone="danger" disabled={!reason.trim()} onClick={() => setConfirming(true)}>
          Cancel the project
        </Button>
      )}
    </Section>
  );
}
